import { useState } from 'react'
import { Menu } from 'lucide-react'
import { NavLink } from 'react-router'
import { Icon } from '../platform/icons/Icon'
import { Overlay } from '../platform/overlay/Overlay'
import { useHitTarget } from '../platform/responsive/hitTarget'
import { MORE_GROUPS, PHONE_TABS, withBadge, type NavBadges } from './nav'
import './shell.css'

/**
 * The phone bottom tab bar — #109.
 *
 * Product overview §6 puts a five-item tab bar below 640px, mirroring iOS, with
 * everything else behind a "More" sheet. Until this, phone got what tablet gets:
 * the sidebar forced to its 68px rail. That is 18% of a 375px viewport spent
 * permanently on navigation, and the rail shows icons whose labels are clipped
 * to 1px — so the destinations are there but unreadable.
 *
 * **Six controls, not five, and the spec says both.** §6's table says "5 items";
 * rule 3 names five destinations AND adds a More sheet. Those cannot all hold
 * at once, and the reading taken here is five destinations plus a More
 * affordance — because dropping one of the five to make room contradicts the
 * explicit list, while iOS's own convention is that More is additional to the
 * named tabs. At 320px six controls are 53px each, which clears M0-R29's 44px.
 * Recorded on the epic as a PRD gap rather than settled here.
 *
 * **The sidebar is not rendered at all at this width** — see `ChromeLayout`,
 * which swaps rather than adds. The reason is the TAB ORDER, not the landmark:
 * the sidebar is `aria-label="Main"` and this is `"Primary"`, so there was never
 * a name collision to avoid. A hidden-but-rendered sidebar leaves its 25 links
 * in the tab order behind a fixed bar, which is why `ChromeLayout.test.tsx`
 * asserts a `.sidebar__link` count of zero rather than querying the landmark.
 * An earlier version of this comment gave the landmark as the reason, which was
 * the weaker of the two and not the one that is load-bearing.
 *
 * **`badges` are the live counts from `useNavBadges`**, read once by
 * `ChromeLayout` and applied here to the More sheet's items (Recurring's overdue
 * count lives there, not on a tab). The sheet draws them; it does not fetch them.
 */
export function BottomTabs({ badges }: { badges?: NavBadges } = {}) {
  const hitTarget = useHitTarget('page')
  const [moreOpen, setMoreOpen] = useState(false)

  return (
    <>
      <nav className="bottom-tabs" aria-label="Primary">
        {PHONE_TABS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            // `end={false}` is the default and is the point: `/transactions/abc`
            // must still mark Transactions. An exact match leaves the bar
            // showing nothing current the moment M6 ships a detail route — the
            // defect ChromeLayout already records for the breadcrumb.
            className="bottom-tabs__tab hoverable"
            style={{ minHeight: hitTarget }}
          >
            <Icon glyph={item.icon} />
            <span className="bottom-tabs__label">{item.label}</span>
          </NavLink>
        ))}

        {/* A button, not a link: it opens a sheet rather than going anywhere,
            and `aria-haspopup="dialog"` says which kind. Announcing it as a
            link would promise a navigation that never happens. */}
        <button
          type="button"
          className="bottom-tabs__tab hoverable"
          style={{ minHeight: hitTarget }}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
        >
          <Icon glyph={Menu} />
          <span className="bottom-tabs__label">More</span>
        </button>
      </nav>

      {/**
       * `placement="right"` rather than a new `bottom` variant.
       *
       * The panel is `min(420px, 100%)`, so it is already full-bleed below
       * 420px — by arithmetic rather than by a breakpoint, which is what
       * `single-source.test.ts` requires. This surface only exists below 640px,
       * so the widest it is ever seen at is a 420px sheet on a 639px viewport,
       * which is an ordinary drawer. Adding a placement to draw the same thing
       * from a different edge would be a change to a shared primitive for one
       * caller's aesthetics.
       */}
      <Overlay
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        label="More destinations"
        placement="right"
      >
        <div className="more-sheet">
          {MORE_GROUPS.map((group) => (
            <section key={group.name} className="more-sheet__group">
              <h2 className="more-sheet__group-name">{group.name}</h2>
              {group.items.map((raw) => {
                const item = withBadge(raw, badges)
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className="more-sheet__link hoverable"
                    style={{ minHeight: hitTarget }}
                    // Closing on choose, because the sheet is a portal over the
                    // whole viewport at this width — left open it covers the
                    // screen the user just asked for.
                    onClick={() => setMoreOpen(false)}
                  >
                    <Icon glyph={item.icon} />
                    <span>{item.label}</span>
                    {/* Same treatment as the sidebar's: plain text inside the
                        link so the count lands in the accessible name, and a
                        badge of 0 is a real answer rather than a falsy one. */}
                    {item.badge !== undefined && (
                      <>
                        {' '}
                        <span className="more-sheet__badge">{item.badge}</span>
                      </>
                    )}
                  </NavLink>
                )
              })}
            </section>
          ))}
        </div>
      </Overlay>
    </>
  )
}
